import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { showErrorToast, showSuccessToast } from "../../../utils";
import {
  useGetMergePreview,
  useMergeDimensionItem,
} from "../../../hooks/query/dimensions/dimensionAdmin";
import { useGetDimensions } from "../../../hooks/query/dimensions/dimensions";

const LEVEL_KEY_MAP = {
  LEVEL_ONE: "dimensionOne",
  LEVEL_TWO: "dimensionTwo",
  LEVEL_THREE: "dimensionThree",
  LEVEL_FOUR: "dimensionFour",
  LEVEL_FIVE: "dimensionFive",
};

function MergeDimensionItemModal({ isOpen, onClose, item, onSuccess }) {
  const toast = useToast();
  const [selectedTargetId, setSelectedTargetId] = useState(null);
  const [stage, setStage] = useState("select"); // "select" | "preview"
  const [searchQuery, setSearchQuery] = useState("");
  const [scrollNode, setScrollNode] = useState(null);
  const scrollRef = useCallback((node) => setScrollNode(node), []);

  const { data: dimensionsData } = useGetDimensions({}, { enabled: isOpen });

  const levelKey = item ? LEVEL_KEY_MAP[item.dimensionLevel] : null;
  const sameDimensionItems = useMemo(() => {
    if (!dimensionsData || !levelKey) return [];
    return (dimensionsData[levelKey]?.items || []).filter(
      (i) => i.id !== item.id && i.state !== "ARCHIVED",
    );
  }, [dimensionsData, levelKey, item?.id]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return sameDimensionItems;
    const q = searchQuery.toLowerCase();
    return sameDimensionItems.filter((i) => i.name.toLowerCase().includes(q));
  }, [sameDimensionItems, searchQuery]);

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => scrollNode,
    estimateSize: () => 42,
    enabled: stage === "select",
  });

  const {
    data: previewData,
    isLoading: isPreviewLoading,
  } = useGetMergePreview(item?.id, selectedTargetId, {
    enabled: stage === "preview" && !!selectedTargetId,
  });

  const { mutate: mergeDimensionItem, isLoading: isMerging } = useMergeDimensionItem({
    onSuccess: (result) => {
      const total = result.questionnairesUpdated + result.directEmissionsUpdated + result.electricityUpdated;
      showSuccessToast(
        toast,
        `Merged successfully. ${total} record(s) re-pointed, ${result.reportsReflagged} report(s) queued for recalculation.`,
      );
      onSuccess();
      onClose();
    },
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const selectedTarget = sameDimensionItems.find((i) => i.id === selectedTargetId);

  const handlePreview = () => {
    if (selectedTargetId) {
      setStage("preview");
    }
  };

  const handleBack = () => {
    setStage("select");
  };

  const handleMerge = () => {
    mergeDimensionItem({ sourceId: item.id, targetId: selectedTargetId });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Merge "{item.name}"
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {stage === "select" ? (
            <Stack spacing={3}>
              <Text fontSize="sm" color="gray.600">
                Select the target {item.dimensionName} item to merge into.
                All data from "{item.name}" ({item.name.length} chars) will be moved to the target.
              </Text>

              {sameDimensionItems.length === 0 ? (
                <Text fontSize="sm" color="gray.400">
                  No other items available in this dimension.
                </Text>
              ) : (
                <Box>
                  <InputGroup size="sm" mb={2}>
                    <InputLeftElement pointerEvents="none">
                      <HiMagnifyingGlass color="gray" />
                    </InputLeftElement>
                    <Input
                      placeholder={`Search ${sameDimensionItems.length} items...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </InputGroup>
                  {filteredItems.length === 0 ? (
                    <Text fontSize="sm" color="gray.400" py={2}>
                      No items match your search.
                    </Text>
                  ) : (
                    <Box ref={scrollRef} maxH="350px" overflowY="auto">
                      <Box style={{ height: `${virtualizer.getTotalSize()}px`, width: "100%", position: "relative" }}>
                        <Box style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${virtualizer.getVirtualItems()[0]?.start ?? 0}px)` }}>
                          {virtualizer.getVirtualItems().map((virtualRow) => {
                            const targetItem = filteredItems[virtualRow.index];
                            return (
                              <Box
                                key={targetItem.id}
                                data-index={virtualRow.index}
                                ref={virtualizer.measureElement}
                                p={2}
                                borderRadius="md"
                                cursor="pointer"
                                bg={selectedTargetId === targetItem.id ? "limeShade.50" : "transparent"}
                                border="1px solid"
                                borderColor={selectedTargetId === targetItem.id ? "limeShade.400" : "gray.200"}
                                _hover={{ bg: "gray.50" }}
                                onClick={() => setSelectedTargetId(targetItem.id)}
                              >
                                <Flex justify="space-between" align="center">
                                  <Text fontSize="sm">{targetItem.name}</Text>
                                  <Badge fontSize="2xs" colorScheme="gray" variant="outline">
                                    {targetItem.name.length} chars
                                  </Badge>
                                </Flex>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Stack>
          ) : (
            <Stack spacing={4}>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>Source (will be archived)</Text>
                <Box p={2} bg="red.50" borderRadius="md" border="1px solid" borderColor="red.200">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="sm">{item.name}</Text>
                    <Badge fontSize="2xs" colorScheme="red" variant="outline">{item.name.length} chars</Badge>
                  </Flex>
                </Box>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>Target (will receive data)</Text>
                <Box p={2} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="sm">{selectedTarget?.name}</Text>
                    <Badge fontSize="2xs" colorScheme="green" variant="outline">{selectedTarget?.name.length} chars</Badge>
                  </Flex>
                </Box>
              </Box>

              {isPreviewLoading ? (
                <Flex justify="center" py={4}>
                  <Spinner size="sm" />
                  <Text ml={2} fontSize="sm" color="gray.500">Loading impact preview...</Text>
                </Flex>
              ) : previewData ? (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>Impact</Text>
                  <Stack spacing={1}>
                    <ImpactRow label="Questionnaires to re-point" count={previewData.questionnairesUpdated} />
                    <ImpactRow label="Direct emissions entries to re-point" count={previewData.directEmissionsUpdated} />
                    <ImpactRow label="Electricity entries to re-point" count={previewData.electricityUpdated} />
                    <ImpactRow label="Source reports to delete" count={previewData.reportsDeleted} />
                    <ImpactRow label="Target reports to recalculate" count={previewData.reportsReflagged} />
                    <ImpactRow label="Relationships to transfer" count={previewData.relationshipsTransferred} />
                  </Stack>
                </Box>
              ) : null}

              <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                This action cannot be undone. The source item will be archived and all its data will be moved to the target.
                Target reports will be recalculated automatically within 10 minutes.
              </Alert>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {stage === "select" ? (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handlePreview}
                isDisabled={!selectedTargetId}
              >
                Preview
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={handleBack} isDisabled={isMerging}>
                Back
              </Button>
              <Button
                colorScheme="red"
                onClick={handleMerge}
                isLoading={isMerging}
                isDisabled={isPreviewLoading}
              >
                Merge
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ImpactRow({ label, count }) {
  return (
    <Flex justify="space-between" fontSize="sm" py={0.5}>
      <Text color="gray.600">{label}</Text>
      <Text fontWeight="semibold">{count}</Text>
    </Flex>
  );
}

export default MergeDimensionItemModal;
